import { z } from 'zod';
import { PlayerIdSchema } from '../ids';
import { TimestampMsSchema } from '../time';
import { QuantitySchema } from './items';

/**
 * A company's ranks, highest first (SOC-PARTY, B105). What each may do is data
 * (`companies.ranks` in the game config); the order is fixed: a member acts only on lower ranks.
 */
export const COMPANY_RANKS = ['leader', 'officer', 'member', 'recruit'] as const;
export const CompanyRankSchema = z.enum(COMPANY_RANKS);
export type CompanyRank = z.infer<typeof CompanyRankSchema>;

/** Whether rank `a` stands above rank `b`. */
export const rankAbove = (a: CompanyRank, b: CompanyRank): boolean =>
  COMPANY_RANKS.indexOf(a) < COMPANY_RANKS.indexOf(b);

export const COMPANY_NAME_MIN_LENGTH = 3;
export const COMPANY_NAME_MAX_LENGTH = 24;

/** Letters in words joined by single spaces, apostrophes or hyphens, like characters' names. */
const COMPANY_NAME_PATTERN = /^\p{L}\p{M}*(?:[ '-]?\p{L}\p{M}*)*$/u;

const normalizeCompanyName = (name: string): string =>
  name.normalize('NFC').trim().replace(/\s+/gu, ' ');

/** Uniqueness key of a company's name: names differing only in case are the same name. */
export const companyNameKey = (name: string): string => normalizeCompanyName(name).toLowerCase();

/** A new company's name, validated after normalization. */
export const CompanyNameSchema = z
  .string()
  .max(COMPANY_NAME_MAX_LENGTH * 4)
  .transform(normalizeCompanyName)
  .pipe(
    z
      .string()
      .min(COMPANY_NAME_MIN_LENGTH)
      .max(COMPANY_NAME_MAX_LENGTH)
      .regex(COMPANY_NAME_PATTERN),
  );

/** A banner: a field colour and an emblem, both indices of the client's palettes. */
export const CompanyBannerSchema = z.object({
  color: z.number().int().min(0).max(15),
  emblem: z.number().int().min(0).max(31),
});
export type CompanyBanner = z.infer<typeof CompanyBannerSchema>;

export const CompanyIdSchema = z.uuid();

export const CompanyMemberSchema = z.object({
  playerId: PlayerIdSchema,
  displayName: z.string(),
  rank: CompanyRankSchema,
  joinedAt: TimestampMsSchema,
});
export type CompanyMember = z.infer<typeof CompanyMemberSchema>;

/** One move of the treasury, as members see it (GR-4: every move is visible to them). */
export const CompanyLedgerEntrySchema = z.object({
  kind: z.enum(['deposit', 'withdraw']),
  playerId: PlayerIdSchema.nullable(),
  amount: QuantitySchema,
  at: TimestampMsSchema,
});
export type CompanyLedgerEntry = z.infer<typeof CompanyLedgerEntrySchema>;

/** A company as its members see it. */
export const CompanySchema = z.object({
  id: CompanyIdSchema,
  name: z.string(),
  banner: CompanyBannerSchema,
  members: z.array(CompanyMemberSchema),
  coins: QuantitySchema,
  history: z.array(CompanyLedgerEntrySchema),
});
export type Company = z.infer<typeof CompanySchema>;

/** A standing invitation, as the invited character sees it. */
export const CompanyInviteSchema = z.object({
  companyId: CompanyIdSchema,
  name: z.string(),
  banner: CompanyBannerSchema,
  invitedBy: z.string(),
  expiresAt: TimestampMsSchema,
});
export type CompanyInvite = z.infer<typeof CompanyInviteSchema>;
